import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Shield,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { UserFormPanel } from '../components/users/UserFormPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const UsersPage = () => {
  const { t } = useTranslation('security');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, [search]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${API_URL}/api/users?${params.toString()}`),
        axios.get(`${API_URL}/api/rbac/roles`)
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('users.messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/users`, data);
      toast.success(t('users.messages.created'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.messages.errorCreating'));
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}`, data);
      toast.success(t('users.messages.updated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.messages.errorUpdating'));
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('users.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${id}`);
      toast.success(t('users.messages.deleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.messages.errorDeleting'));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Work_Sans',sans-serif]">
            {t('users.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('users.subtitle')}
          </p>
        </div>
        <Button 
          className="bg-[#E63946] hover:bg-[#C92A36]"
          onClick={() => { setSelectedUser(null); setIsFormOpen(true); }}
          data-testid="add-user-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('users.newUser')}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('users.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="user-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border" data-testid="users-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.table.user')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.table.email')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.table.roles')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.table.status')}</th>
                    <th className="px-4 py-3 bg-muted text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-border">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                        {t('users.table.noUsers')}
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-[#E63946] flex items-center justify-center text-white font-medium">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                            <div>
                              <div className="font-medium text-foreground">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground">{user.phone}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {user.email}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {user.roles?.map((role) => (
                              <Badge key={role.id} variant="outline" className="text-xs">
                                <Shield className="h-3 w-3 mr-1" />
                                {role.name}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            user.status === 'active' 
                              ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/50 dark:text-green-300' 
                              : user.status === 'suspended'
                              ? 'bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/50 dark:text-red-300'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300'
                          }>
                            {t(`users.status.${user.status}`)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedUser(user); setIsFormOpen(true); }}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(user.id)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Panel */}
      <UserFormPanel
        user={selectedUser}
        roles={roles}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={selectedUser ? handleUpdate : handleCreate}
      />
    </div>
  );
};

export default UsersPage;
